import { Component } from '@angular/core';
import { combineLatest } from 'rxjs';
import { take } from 'rxjs/operators';
import { Queue } from 'api/types';
import { TranslatePipe } from 'pipes/translate.pipe';
import { QueuesService } from 'services/api/queues.service';
import { Filter } from 'types/Filter';
import { formatQueueDisplay } from 'utils/format-queue';
import { GetPoolConsumptionParameters } from 'api/types/endpoints/getPoolConsumption';
import { PoolConsumptionPageDataService } from 'services/poolconsumption-page-data.service';

export interface PoolConsumptionFilters {
  queue: Filter<Queue>;
}

/**
 *  Filters for the clients page
 */
@Component({
    selector: 'app-poolconsumption-filters',
    templateUrl: './poolconsumption-filters.component.html',
    styleUrls: ['./poolconsumption-filters.component.scss'],
    standalone: false
})

export class PoolConsumptionFiltersComponent {
  /**
   * Used to signal the special "All Queues" option
   */
  private static readonly ALL_QUEUES_CODE = 'ALLQUEUES';

  /**
   * Initial search term
   */
  public initialSearchTerm = '';

  /**
   * How the queue should look as a dropdown option
   */
  public getQueueDisplay = formatQueueDisplay;

  /**
   * List of available queues
   */
  public queues: Queue[] = [];

  /**
   * Queue filter
   */
  public filters: PoolConsumptionFilters = {
    queue: {
      enabled: true,
    }
  };

  public constructor(
    private poolConsumptionPageDataService: PoolConsumptionPageDataService,
    private queuesService: QueuesService,
    private translatePipe: TranslatePipe,
  ) {
    // Load queues & translation & previous filters
    combineLatest([
      this.queuesService.getQueues(),
      this.translatePipe.loadTranslations([ 'option.allQueues' ]),
      this.poolConsumptionPageDataService.params$
    ])
      .pipe(take(1))
      .subscribe(([ queueResponse, translations, params ]) => {
        // Create a filter option for all queues
        const allQueuesOption: Queue = {
          id: 'all',
          code: PoolConsumptionFiltersComponent.ALL_QUEUES_CODE,
          name: translations[ 'option.allQueues' ]
        };
        this.initialSearchTerm = params?.searchTerm || '';
        this.queues = [ allQueuesOption, ...queueResponse ];

        // Set initial queue from service or default to all queues
        this.filters.queue.value = this.queues.find((q) => params?.queueIds?.includes(q.id)) ?? allQueuesOption;
        this.filters.queue.options = this.queues;
      });
  }

  /**
   * Update client search param
   */
  public updateSearchParams(inputValue: string): void {
    const newParams: Partial<GetPoolConsumptionParameters> = {
      searchTerm: inputValue,
    };
    this.poolConsumptionPageDataService.updateParams(newParams);
  }

  /**
   * Handle change in queue filter
   *
   * Note: there is only a single filter, so `filterItem` is ignored.
   */
  public filterChanged(filterItem: string, queueInfo?: Queue): void {
    // Pass empty array for all queues
    const ids = !queueInfo || queueInfo.code === PoolConsumptionFiltersComponent.ALL_QUEUES_CODE ? [] : [ queueInfo.id ];
    const newParams: Partial<GetPoolConsumptionParameters> = {
      queueIds: ids,
    };
    this.poolConsumptionPageDataService.updateParams(newParams);
  }
}
